export const LOCKER_METHODS = new Set(["foxpostLocker", "glsLocker"]);
export const HOME_METHODS = new Set(["foxpostHome", "glsHome"]);

const PICKUP_BILLING_ADDRESS = {
  street: "Fáy utca",
  houseNumber: "75",
  postalCode: "1135",
  city: "Budapest",
  country: "Hungary",
};

export function buildInitialBilling(customer, billingAddress, shippingMethod) {
  const useCollectedAddress =
    HOME_METHODS.has(shippingMethod) || shippingMethod === "pickup";
  const address = billingAddress || {};

  return {
    name: customer.name || "",
    email: customer.email || "",
    street: useCollectedAddress ? address.street || "" : PICKUP_BILLING_ADDRESS.street,
    houseNumber: useCollectedAddress
      ? address.houseNumber || ""
      : PICKUP_BILLING_ADDRESS.houseNumber,
    postalCode: useCollectedAddress
      ? address.postalCode || ""
      : PICKUP_BILLING_ADDRESS.postalCode,
    city: useCollectedAddress ? address.city || "" : PICKUP_BILLING_ADDRESS.city,
    country: "Hungary",
  };
}

export function isAddressComplete(address) {
  if (!address) return false;
  return Boolean(
    address.postalCode?.trim() && address.city?.trim() && address.street?.trim(),
  );
}

export function isBillingComplete(billing) {
  if (!billing) return false;

  return Boolean(
    billing.name?.trim() &&
      billing.email?.trim() &&
      billing.street?.trim() &&
      billing.city?.trim() &&
      billing.postalCode?.trim(),
  );
}

export function formatHuf(amount) {
  const value = Math.round(Number(amount) || 0);
  return `${value}.-`;
}

/** Turn Foxpost HTML snippets (findme, etc.) into readable plain text. */
export function sanitizeFoxpostHtml(value) {
  if (typeof value !== "string" || !value) return "";

  return value
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/\s*p\s*>/gi, "\n")
    .replace(/<\s*p[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Keep location note only — drop payment/service blocks Foxpost appends to findme. */
export function sanitizeFoxpostFindme(value) {
  let cleaned = sanitizeFoxpostHtml(value);
  if (!cleaned) return "";

  const cutMarkers = [
    /Fizetési lehetőség\s*:/i,
    /Elérhető szolgáltatások\s*:/i,
    /Payment options?\s*:/i,
    /Available services?\s*:/i,
  ];

  let cutAt = cleaned.length;
  for (const marker of cutMarkers) {
    const match = cleaned.search(marker);
    if (match !== -1 && match < cutAt) {
      cutAt = match;
    }
  }

  return cleaned.slice(0, cutAt).replace(/\n{3,}/g, "\n\n").trim();
}

export function needsLocationStep(shippingMethod) {
  return LOCKER_METHODS.has(shippingMethod) || HOME_METHODS.has(shippingMethod);
}

export function getStepTitle(step, t) {
  if (step === "shipping") return t.shippingTitle;
  if (step === "location") return t.locationTitle;
  if (step === "contact") return t.contactTitle;
  return t.paymentTitle;
}

export function getNextStep(step, shippingMethod) {
  if (step === "shipping") {
    return needsLocationStep(shippingMethod) ? "location" : "contact";
  }
  if (step === "location") return "contact";
  if (step === "contact") return "payment";
  return step;
}

export function getPrevStep(step, shippingMethod) {
  if (step === "payment") return "contact";
  if (step === "contact") {
    return needsLocationStep(shippingMethod) ? "location" : "shipping";
  }
  if (step === "location") return "shipping";
  return "shipping";
}

export function formatPickupSummary(pickupPoint, shippingMethod) {
  if (!pickupPoint) return "";

  if (shippingMethod === "foxpostLocker") {
    return [
      sanitizeFoxpostHtml(pickupPoint.name),
      sanitizeFoxpostHtml(pickupPoint.address),
      sanitizeFoxpostFindme(pickupPoint.findme),
    ]
      .filter(Boolean)
      .join(" — ");
  }

  if (shippingMethod === "glsLocker") {
    const contact = pickupPoint.contact || {};
    return [
      pickupPoint.name,
      [contact.postalCode, contact.city, contact.address].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(" — ");
  }

  return pickupPoint.name || "";
}

export function formatAddressSummary(address) {
  if (!address) return "";
  return [
    [address.street, address.houseNumber].filter(Boolean).join(" "),
    [address.postalCode, address.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
}
